import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import VideoCard from '@/components/feature/VideoCard';
import { videos, categories } from '@/mocks/videos';

export default function Article() {
  const { slug } = useParams<{ slug: string }>();
  const { t } = useTranslation();

  const video = videos.find((v) => v.slug === slug);
  const category = video ? categories.find((c) => c.slug === video.category) : null;
  const relatedVideos = video
    ? videos.filter((v) => v.category === video.category && v.id !== video.id).slice(0, 4)
    : [];

  if (!video) {
    return (
      <>
        <Navbar />
        <main className="pt-16 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="w-20 h-20 flex items-center justify-center mx-auto mb-4">
              <i className="ri-error-warning-line text-foreground-400 text-5xl"></i>
            </div>
            <h1 className="text-xl font-bold text-foreground-700 mb-2">影片未找到</h1>
            <p className="text-foreground-400 mb-6">您访问的影片不存在或已被移除</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-foreground-950 font-semibold px-6 py-2.5 rounded-full text-sm transition-all duration-300 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-home-4-line"></i> 返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <ScrollToTop />
      <Navbar />
      <main className="pt-16">
        <div className="max-w-[1200px] mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: t('nav.category'), to: '/category/all' },
              ...(category ? [{ label: category.name, to: `/category/${category.slug}` }] : []),
              { label: video.title },
            ]}
          />

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 pb-14">
            <div className="lg:col-span-2">
              <div className="relative aspect-video rounded-xl overflow-hidden bg-background-200">
                <img
                  src={video.thumbnail}
                  alt={video.title}
                  title={`${video.title} - 91高清视频在线观看`}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-background-50/60 via-transparent to-transparent"></div>
                <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between">
                  <span className="bg-background-50/80 backdrop-blur-sm text-foreground-800 text-sm px-3 py-1 rounded-full whitespace-nowrap">
                    {video.duration}
                  </span>
                  <button className="w-14 h-14 flex items-center justify-center rounded-full bg-primary-500 hover:bg-primary-600 transition-all duration-300 hover:scale-110 cursor-pointer">
                    <i className="ri-play-fill text-foreground-950 text-3xl ml-1"></i>
                  </button>
                  <span className="bg-primary-500 text-foreground-950 text-sm font-semibold px-3 py-1 rounded-full whitespace-nowrap">
                    {video.quality}
                  </span>
                </div>
              </div>

              <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading mt-6 mb-3">
                {video.title}
              </h1>

              <div className="flex items-center gap-4 flex-wrap mb-6">
                <div className="flex items-center gap-1">
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-star-fill text-accent-500"></i>
                  </div>
                  <span className="text-accent-500 font-bold text-lg">{video.rating}</span>
                </div>
                <span className="text-foreground-400 text-sm">|</span>
                <span className="text-foreground-500 text-sm whitespace-nowrap">{video.year}年</span>
                <span className="text-foreground-400 text-sm">|</span>
                <span className="text-foreground-500 text-sm whitespace-nowrap">
                  <i className="ri-eye-line mr-1"></i>{video.views.toLocaleString()} 次播放
                </span>
                {category && (
                  <>
                    <span className="text-foreground-400 text-sm">|</span>
                    <Link
                      to={`/category/${category.slug}`}
                      className="text-primary-500 hover:text-primary-400 text-sm transition-colors cursor-pointer whitespace-nowrap"
                    >
                      {category.name}
                    </Link>
                  </>
                )}
              </div>

              <div className="flex gap-2 mb-6 flex-wrap">
                {video.tags.map((tag) => (
                  <span
                    key={tag}
                    className="bg-secondary-100 text-secondary-800 text-xs px-3 py-1 rounded-full whitespace-nowrap"
                  >
                    #{tag}
                  </span>
                ))}
              </div>

              <div className="bg-background-200/60 rounded-xl p-5">
                <h2 className="text-lg font-semibold text-foreground-900 mb-3 font-heading">影片简介</h2>
                <p className="text-foreground-500 text-sm leading-relaxed">{video.description}</p>
              </div>
            </div>

            <div className="lg:col-span-1">
              <h3 className="text-lg font-semibold text-foreground-900 mb-4 font-heading">
                {t('video.related')}
              </h3>
              <div className="space-y-3">
                {relatedVideos.length > 0 ? (
                  relatedVideos.map((v) => (
                    <VideoCard key={v.id} video={v} />
                  ))
                ) : (
                  videos
                    .filter((v) => v.id !== video.id)
                    .slice(0, 4)
                    .map((v) => (
                      <VideoCard key={v.id} video={v} />
                    ))
                )}
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}